import { Component, HostListener, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { ErrorLogAnalysisReportService } from '../../../business-services/error-log-analysis-report.service';
import { ErrorLogAnalysisModel, ErrorLogOccurrenceListModel, ErrorLogOccurrenceModel, ErrorLogStatus, ErrorLogStatusList } from '../../../business-entities/error-log-analysis-report.model';
import { copyText, getAnalysisRequest, getCauseText, getThrownAt, getWindowRange, toLocalDate } from './error-log-analysis.helper';
import { PaginationService } from '../../../services/pagination.service';
import { PagerData } from '../../../business-entities/PagerData';
import { ErrorLogEndpointDetailsComponent } from './error-log-endpoint-details/error-log-endpoint-details.component';

@Component({
  selector: 'app-error-log-analysis-report',
  templateUrl: './error-log-analysis-report.component.html',
  styleUrls: ['../report-dashboard.css', './error-log-shared.css', './error-log-analysis-report.component.css']
})
export class ErrorLogAnalysisReportComponent implements OnInit {

  readonly occurrencePageSize: number = 50;

  searchTerm: string = '';
  appliedSearchTerm: string = '';
  dateRange: Date[] = [];
  selectedStatus: ErrorLogStatus = null;
  // Set by clicking a row of Top Endpoints / Probable Causes.
  selectedEndpoint: string = null;
  selectedEndpointLabel: string = '';
  selectedCause: string = null;
  pager: any = {};
  pagerData: PagerData = { page: 1, pagerSize: 20 };
  readonly statusList = ErrorLogStatusList;

  spikeList: ErrorLogAnalysisModel[] = [];
  displayList: ErrorLogAnalysisModel[] = [];

  stats: any = { endpoints: 0 };
  endpointItems: any[] = [];
  causeItems: any[] = [];

  // Quick view (right panel)
  selectedItem: ErrorLogAnalysisModel = null;
  panelOccurrences: ErrorLogOccurrenceModel[] = [];
  panelTotal: number = 0;
  panelLoading: boolean = false;
  private panelRequestNo: number = 0;

  readonly toLocalDate = toLocalDate;
  readonly getCauseText = getCauseText;
  readonly getThrownAt = getThrownAt;

  myDateRangePickerOptions: Partial<BsDaterangepickerConfig> = Object.assign({}, {
    containerClass: 'theme-dark-blue',
    displayMonths: 1,
    showWeekNumbers: false,
    rangeInputFormat: 'DD/MM/YYYY',
    customTodayClass: 'custom-today-class',
    maxDateRange: 366
  });

  constructor(
    private service: ErrorLogAnalysisReportService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private modalService: NgbModal,
    private pageService: PaginationService
  ) { }

  ngOnInit(): void {
    this.setDefaultDateRange();
    this.getReportList();
  }

  setDefaultDateRange() {
    let today = new Date();
    this.dateRange = [new Date(today.getFullYear(), today.getMonth(), 1), today];
  }

  onDateRangeChange() {
    if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
      return;
    }
    if (!this.isValidDateRange()) {
      this.toastr.error(this.translate.instant('lblMaxOneYearRange'));
      this.setDefaultDateRange();
      return;
    }
    this.getReportList();
  }

  isValidDateRange(): boolean {
    let limit = new Date(this.dateRange[0]);
    limit.setFullYear(limit.getFullYear() + 1);
    return new Date(this.dateRange[1]) <= limit;
  }

  onReset() {
    this.searchTerm = '';
    this.appliedSearchTerm = '';
    this.selectedStatus = null;
    this.selectedEndpoint = null;
    this.selectedCause = null;
    this.setDefaultDateRange();
    this.getReportList();
  }

  async getReportList() {
    if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
      return;
    }
    this.spinner.show();
    await this.service.GetErrorLogAnalysis(getAnalysisRequest(this.dateRange)).then((res: any) => {
      let data: ErrorLogAnalysisModel[] = res?.responseObject;
      this.spikeList = Array.isArray(data) ? data : [];
      this.applyFilters();
      this.refreshSelectedItem();
    }).finally(() => {
      this.spinner.hide();
    });
  }

  // ---------- Filters ----------
  onQuickSearch() {
    let term = this.searchTerm ? this.searchTerm.toString().trim() : '';
    this.appliedSearchTerm = term.length > 2 ? term : '';
    this.applyFilters();
  }

  applyFilters() {
    let term = this.appliedSearchTerm.toLowerCase();
    let filtered = this.spikeList.filter((item) =>
      (!this.selectedStatus || item.status === this.selectedStatus) &&
      (!term || this.isSearchMatch(item, term)));
    // The charts keep showing every endpoint / cause of the other filters, so they stay clickable.
    this.buildInsights(filtered);
    // Highest occurrences first, then newest.
    this.displayList = filtered
      .filter((item) =>
        (!this.selectedEndpoint || this.getEndpoint(item) === this.selectedEndpoint) &&
        (!this.selectedCause || this.getCauseTitle(item.cause) === this.selectedCause))
      .sort((a, b) => b.occurrenceCount - a.occurrenceCount || b.windowStart.localeCompare(a.windowStart));
    this.setPageNumber(1);
  }

  // Click on a chart row filters the list to it; a second click clears it.
  onEndpointClick(name: string, label: string = '') {
    this.selectedEndpoint = this.selectedEndpoint === name ? null : name;
    this.selectedEndpointLabel = this.selectedEndpoint ? label : '';
    this.applyFilters();
  }

  onCauseClick(name: string) {
    this.selectedCause = this.selectedCause === name ? null : name;
    this.applyFilters();
  }

  isSearchMatch(item: ErrorLogAnalysisModel, term: string): boolean {
    return [item.controllerName, item.methodName, item.errorMessage, item.cause]
      .some((value) => value && value.toString().toLowerCase().indexOf(term) > -1);
  }

  // ---------- Insights ----------
  buildInsights(list: ErrorLogAnalysisModel[]) {
    let endpointMap: any = {};
    let causeMap: any = {};
    list.forEach((item) => {
      let endpoint = this.getEndpoint(item);
      if (!endpointMap[endpoint]) {
        endpointMap[endpoint] = { name: endpoint, controllerName: item.controllerName, methodName: item.methodName, count: 0, spikes: 0 };
      }
      endpointMap[endpoint].count += item.occurrenceCount;
      endpointMap[endpoint].spikes += 1;

      let cause = this.getCauseTitle(item.cause);
      causeMap[cause] = (causeMap[cause] ? causeMap[cause] : 0) + 1;
    });

    let endpoints = Object.keys(endpointMap).map((key) => endpointMap[key]).sort((a, b) => b.count - a.count);
    let highestEndpoint = endpoints.length ? endpoints[0].count : 1;
    this.endpointItems = endpoints.slice(0, 20).map((item) => ({ ...item, percent: Math.round((item.count / highestEndpoint) * 100) }));

    let causes = Object.keys(causeMap).map((key) => ({ name: key, count: causeMap[key] })).sort((a, b) => b.count - a.count);
    let highestCause = causes.length ? causes[0].count : 1;
    this.causeItems = causes.slice(0, 20).map((item) => ({ ...item, percent: Math.round((item.count / highestCause) * 100) }));

    this.stats = {
      endpoints: endpoints.length
    };
  }

  onStatusFilterChange() {
    this.applyFilters();
  }

  // ---------- Table ----------
  get visibleList(): ErrorLogAnalysisModel[] {
    return this.displayList.slice(this.pager.startIndex, this.pager.endIndex + 1);
  }

  setPageNumber(page: number) {
    this.pager = this.pageService.getPager(this.displayList.length, page, this.pagerData.pagerSize);
    this.pagerData.page = page;
  }

  setPage(pagerData: PagerData) {
    if (!pagerData || pagerData.page < 1 || pagerData.page > this.pager.totalPages) {
      return;
    }
    this.pagerData.pagerSize = pagerData.pagerSize;
    this.setPageNumber(pagerData.page);
  }

  getEndpoint(item: ErrorLogAnalysisModel): string {
    return item.controllerName + '.' + item.methodName;
  }

  getRowKey(item: ErrorLogAnalysisModel): string {
    return this.getEndpoint(item) + '|' + item.windowStart;
  }

  isSelected(item: ErrorLogAnalysisModel): boolean {
    return !!this.selectedItem && this.getRowKey(this.selectedItem) === this.getRowKey(item);
  }

  // Controller / Method name opens the endpoint details popup for the same date range.
  openEndpoint(item: ErrorLogAnalysisModel, event: Event) {
    event.stopPropagation();
    const modalref = this.modalService.open(ErrorLogEndpointDetailsComponent, {
      size: 'lg',
      windowClass: 'EditPopup',
    });
    modalref.componentInstance.controllerName = item.controllerName;
    modalref.componentInstance.methodName = item.methodName;
    modalref.componentInstance.dateRange = [...this.dateRange];
    modalref.result.then((response) => {
      if (response?.statusChanged) {
        this.getReportList();
      }
    }, () => { });
  }

  // ---------- Quick view ----------
  get selectedIndex(): number {
    return this.selectedItem ? this.displayList.findIndex((item) => this.isSelected(item)) : -1;
  }

  openPanel(item: ErrorLogAnalysisModel) {
    if (this.isSelected(item)) {
      return;
    }
    this.selectedItem = item;
    this.loadPanelOccurrences(false);
  }

  closePanel() {
    this.selectedItem = null;
    this.panelOccurrences = [];
    this.panelTotal = 0;
    this.panelLoading = false;
    this.panelRequestNo++;
  }

  movePanel(step: number) {
    let index = this.selectedIndex + step;
    if (index < 0 || index >= this.displayList.length) {
      return;
    }
    let page = Math.floor(index / this.pagerData.pagerSize) + 1;
    if (page !== this.pager.currentPage) {
      this.setPageNumber(page);
    }
    this.openPanel(this.displayList[index]);
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    // Esc in the endpoint details popup closes only the popup.
    if (this.selectedItem && !this.modalService.hasOpenModals()) {
      this.closePanel();
    }
  }

  // After a reload keep the panel on the same spike, or close it when the spike is gone.
  refreshSelectedItem() {
    if (!this.selectedItem) {
      return;
    }
    let key = this.getRowKey(this.selectedItem);
    let match = this.spikeList.find((item) => this.getRowKey(item) === key);
    if (match) {
      this.selectedItem = match;
      this.loadPanelOccurrences(false);
    } else {
      this.closePanel();
    }
  }

  async loadPanelOccurrences(append: boolean) {
    let item = this.selectedItem;
    let requestNo = ++this.panelRequestNo;
    if (!append) {
      this.panelOccurrences = [];
      this.panelTotal = 0;
    }
    this.panelLoading = true;
    await this.service.GetErrorLogOccurrences({
      controllerName: item.controllerName,
      methodName: item.methodName,
      ...getWindowRange(item.windowStart),
      skip: this.panelOccurrences.length,
      take: this.occurrencePageSize
    }).then((res: any) => {
      // A newer request (another row) replaced this one.
      if (requestNo !== this.panelRequestNo) {
        return;
      }
      let data: ErrorLogOccurrenceListModel = res?.responseObject;
      this.panelOccurrences = this.panelOccurrences.concat(data?.items || []);
      this.panelTotal = data?.totalCount || 0;
    }).finally(() => {
      if (requestNo === this.panelRequestNo) {
        this.panelLoading = false;
      }
    });
  }

  // Copy button of a quick view block.
  async copy(text: string) {
    if (await copyText(text || '')) {
      this.toastr.success(this.translate.instant('lblCopiedToClipboard'));
    } else {
      this.toastr.error(this.translate.instant('lblCopyFailed'));
    }
  }

  // Rule text ends with "Thrown at: ..." - keep only the readable cause for chart grouping.
  getCauseTitle(cause: string): string {
    let text = getCauseText(cause);
    return text.length > 70 ? text.substring(0, 67) + '...' : text;
  }

}
